import {
  createStandardPublicClientApplication,
  LogLevel,
  type Configuration,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { useEffect, useState, type ReactNode } from 'react';
import { type IAgxConfig, type IApiConfig } from '../config/Config';
import AuthProvider from './AuthProvider';

const CreateAuthConfig = ({
  authClientId,
  authInstance,
  authDomain,
  authSignupSigninPolicyId,
  envSigninPrefix,
  authEnableLogs,
}: IAgxConfig): Configuration => {
  if (!authClientId) throw new Error('authClientId is not set');
  if (!authInstance) throw new Error('authInstance is not set');
  if (!authDomain) throw new Error('authDomain is not set');
  if (!authSignupSigninPolicyId) throw new Error('authSignupSigninPolicyId is not set');
  if (!envSigninPrefix) throw new Error('envSigninPrefix is not set');

  const enableLogs = authEnableLogs === 'true';

  return {
    auth: {
      clientId: authClientId,
      authority: `https://${authInstance}/${authDomain}/${authSignupSigninPolicyId}`,
      knownAuthorities: [authInstance],
      redirectUri: envSigninPrefix,
    },
    cache: {
      cacheLocation: 'sessionStorage', // This configures where your cache will be stored
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (!enableLogs) return;
          if (containsPii) {
            return;
          }

          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              return;
          }
        },
      },
    },
  };
};

const AgxAuth = ({ children, apiConfig }: { children: ReactNode; apiConfig: IApiConfig }) => {
  const [msalInstance, setMsalInstance] = useState<IPublicClientApplication | undefined>(undefined);

  useEffect(() => {
    const msalConfig = CreateAuthConfig(apiConfig);

    createStandardPublicClientApplication(msalConfig).then((initializedMsalInstance) => {
      setMsalInstance(initializedMsalInstance);
    });
  }, [apiConfig]);

  if (!msalInstance) return null;

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider apiConfig={apiConfig}>{children}</AuthProvider>
    </MsalProvider>
  );
};

export default AgxAuth;
